// src/components/pages/Search/SearchResultsPage.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
  Divider,
  List,
  ListItemButton,
  Chip,
  alpha,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useSearchParams } from "react-router-dom";

import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import NorthWestRoundedIcon from "@mui/icons-material/NorthWestRounded";

import { getLangFromCookie } from "../../Utils/LangCookie";
import {
  buildSiteSearchIndex,
  searchSiteIndex,
  getSearchTypeLabel,
} from "./SiteSearchIndex";

const GROUP_ORDER = [
  "product",
  "variant",
  "brand",
  "category",
  "category_group",
  "news",
  "page",
  "menu",
];

function safeTrim(value) {
  return String(value ?? "").trim();
}

function normalizeLang(lang) {
  const s = String(lang || "").toUpperCase().trim();
  return s === "EN" || s === "JP" || s === "ZH" ? s : "ZH";
}

function getPageText(lang) {
  const L = normalizeLang(lang);

  if (L === "EN") {
    return {
      title: "Search",
      inputPlaceholder: "Search inside SehoPro",
      resultPrefix: "Search results for",
      noQuery: "Enter a keyword to search SehoPro products, brands, categories, news, and pages.",
      noResultTitle: "No matching content found",
      noResultDesc: "Try another keyword or search by product number, brand, category, or page name.",
      loading: "Searching SehoPro...",
      open: "Open",
    };
  }

  if (L === "JP") {
    return {
      title: "検索",
      inputPlaceholder: "SehoPro内を検索",
      resultPrefix: "検索結果",
      noQuery: "キーワードを入力して、商品・ブランド・カテゴリ・ニュース・ページを検索してください。",
      noResultTitle: "一致するコンテンツが見つかりません",
      noResultDesc: "別のキーワード、商品番号、ブランド、カテゴリ、ページ名で検索してください。",
      loading: "SehoProを検索中...",
      open: "開く",
    };
  }

  return {
    title: "搜尋",
    inputPlaceholder: "搜尋 SehoPro 站內內容",
    resultPrefix: "搜尋結果",
    noQuery: "請輸入關鍵字搜尋 SehoPro 商品、品牌、分類、最新資訊與頁面。",
    noResultTitle: "找不到符合的內容",
    noResultDesc: "請嘗試其他關鍵字，或使用產品編號、品牌、分類、頁面名稱搜尋。",
    loading: "正在搜尋 SehoPro...",
    open: "前往",
  };
}

function groupResults(results) {
  const grouped = new Map();

  for (const result of Array.isArray(results) ? results : []) {
    const type = result?.type || "page";
    if (!grouped.has(type)) grouped.set(type, []);
    grouped.get(type).push(result);
  }

  return GROUP_ORDER.map((type) => ({
    type,
    items: grouped.get(type) || [],
  })).filter((group) => group.items.length > 0);
}

function makeResultSubtitle(result, lang) {
  const typeLabel = getSearchTypeLabel(result?.type, lang);
  const subtitle = safeTrim(result?.subtitle);

  if (!subtitle) return typeLabel;
  if (subtitle === typeLabel) return typeLabel;
  if (subtitle.startsWith(`${typeLabel} ·`)) return subtitle;

  return `${typeLabel} · ${subtitle}`;
}

export default function SearchResultsPage({ fonts }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [lang, setLang] = useState(() => normalizeLang(getLangFromCookie()));
  const [inputValue, setInputValue] = useState(() => safeTrim(searchParams.get("q")));
  const [index, setIndex] = useState([]);
  const [loading, setLoading] = useState(true);

  const query = safeTrim(searchParams.get("q"));
  const text = getPageText(lang);
  const fontFamily = fonts?.[lang] || "inherit";

  useEffect(() => {
    const updateLang = () => {
      setLang(normalizeLang(getLangFromCookie()));
    };

    window.addEventListener("app:languageChanged", updateLang);
    return () => window.removeEventListener("app:languageChanged", updateLang);
  }, []);

  useEffect(() => {
    setInputValue(query);
  }, [query]);

  useEffect(() => {
    let cancelled = false;

    setLoading(true);

    buildSiteSearchIndex({ lang })
      .then((nextIndex) => {
        if (cancelled) return;
        setIndex(Array.isArray(nextIndex) ? nextIndex : []);
      })
      .catch(() => {
        if (cancelled) return;
        setIndex([]);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [lang]);

  const results = useMemo(() => {
    if (!query) return [];
    return searchSiteIndex(index, query, { limit: 80 });
  }, [index, query]);

  const groupedResults = useMemo(() => groupResults(results), [results]);

  const handleSubmit = (event) => {
    event?.preventDefault?.();

    const nextQuery = safeTrim(inputValue);

    if (!nextQuery) {
      setSearchParams({});
      return;
    }

    setSearchParams({ q: nextQuery });
  };

  const handleOpenResult = (result) => {
    const url = safeTrim(result?.url);
    if (!url) return;

    navigate(url, {
      state: {
        fromSearchResultsPage: true,
        searchQuery: query,
        searchResultType: result?.type || "",
      },
    });
  };

  return (
    <Box
      sx={{
        bgcolor: "#fffffc",
        minHeight: "100%",
        py: { xs: 5, md: 8 },
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ mb: { xs: 3, md: 4 } }}>
          <Typography
            component="h1"
            sx={{
              fontFamily,
              fontSize: { xs: 30, md: 42 },
              fontWeight: 800,
              letterSpacing: 0.3,
              mb: 2,
            }}
          >
            {text.title}
          </Typography>

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              value={inputValue}
              placeholder={text.inputPlaceholder}
              onChange={(event) => setInputValue(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon sx={{ opacity: 0.7 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {loading && (
                      <CircularProgress
                        size={18}
                        thickness={5}
                        sx={{ mr: 1, opacity: 0.75 }}
                      />
                    )}

                    <IconButton type="submit" edge="end" aria-label="search">
                      <SearchRoundedIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                maxWidth: 760,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 999,
                  bgcolor: "background.paper",
                  boxShadow: (t) =>
                    `0 10px 30px ${alpha(t.palette.common.black, 0.08)}`,
                },
                "& input": {
                  fontFamily,
                  fontSize: { xs: 16, md: 18 },
                  py: { xs: 1.35, md: 1.6 },
                },
              }}
            />
          </Box>
        </Box>

        {!query && !loading && (
          <Box
            sx={{
              maxWidth: 760,
              p: { xs: 2, md: 3 },
              borderRadius: 3,
              bgcolor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography sx={{ fontFamily, fontSize: 15, opacity: 0.75 }}>
              {text.noQuery}
            </Typography>
          </Box>
        )}

        {query && (
          <Box sx={{ mb: 2.5 }}>
            <Typography
              sx={{
                fontFamily,
                fontSize: { xs: 15, md: 16 },
                opacity: 0.75,
              }}
            >
              {text.resultPrefix}：
              <Box component="span" sx={{ fontWeight: 800, opacity: 1 }}>
                {query}
              </Box>
            </Typography>
          </Box>
        )}

        {loading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, py: 4 }}>
            <CircularProgress size={22} thickness={5} />
            <Typography sx={{ fontFamily, fontSize: 14, opacity: 0.75 }}>
              {text.loading}
            </Typography>
          </Box>
        )}

        {!loading && query && results.length === 0 && (
          <Box
            sx={{
              maxWidth: 760,
              p: { xs: 2, md: 3 },
              borderRadius: 3,
              bgcolor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography
              sx={{
                fontFamily,
                fontSize: 17,
                fontWeight: 800,
                mb: 0.75,
              }}
            >
              {text.noResultTitle}
            </Typography>
            <Typography sx={{ fontFamily, fontSize: 14, opacity: 0.72 }}>
              {text.noResultDesc}
            </Typography>
          </Box>
        )}

        {!loading &&
          groupedResults.map((group) => (
            <Box
              key={group.type}
              sx={{
                maxWidth: 900,
                mb: { xs: 2.5, md: 3 },
                borderRadius: 3,
                overflow: "hidden",
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                boxShadow: (t) =>
                  `0 8px 26px ${alpha(t.palette.common.black, 0.06)}`,
              }}
            >
              <Box
                sx={{
                  px: { xs: 1.5, sm: 2 },
                  py: 1.25,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <Typography
                  sx={{
                    fontFamily,
                    fontSize: 15,
                    fontWeight: 800,
                  }}
                >
                  {getSearchTypeLabel(group.type, lang)}
                </Typography>

                <Chip
                  size="small"
                  label={group.items.length}
                  sx={{
                    height: 24,
                    fontFamily,
                    fontWeight: 700,
                  }}
                />
              </Box>

              <Divider />

              <List disablePadding>
                {group.items.map((result, indexInGroup) => (
                  <React.Fragment key={`${result.id || result.url}-${indexInGroup}`}>
                    <ListItemButton
                      onClick={() => handleOpenResult(result)}
                      sx={{
                        px: { xs: 1.5, sm: 2 },
                        py: { xs: 1.25, sm: 1.45 },
                        gap: 1.25,
                        alignItems: "center",
                        "&:hover .seho-search-result-open": {
                          opacity: 1,
                          transform: "translateX(0)",
                        },
                      }}
                    >
                      <SearchRoundedIcon
                        fontSize={isMobile ? "small" : "medium"}
                        sx={{ opacity: 0.58, flexShrink: 0 }}
                      />

                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                          noWrap
                          sx={{
                            fontFamily,
                            fontSize: { xs: 15, md: 16 },
                            fontWeight: 800,
                            lineHeight: 1.35,
                          }}
                        >
                          {result.title}
                        </Typography>

                        <Typography
                          noWrap
                          sx={{
                            fontFamily,
                            fontSize: { xs: 12.5, md: 13 },
                            opacity: 0.68,
                            lineHeight: 1.35,
                          }}
                        >
                          {makeResultSubtitle(result, lang)}
                        </Typography>
                      </Box>

                      <Box
                        className="seho-search-result-open"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          opacity: { xs: 0.75, md: 0.45 },
                          transform: { xs: "none", md: "translateX(4px)" },
                          transition: "opacity .15s ease, transform .15s ease",
                          flexShrink: 0,
                        }}
                      >
                        {!isMobile && (
                          <Typography
                            sx={{
                              fontFamily,
                              fontSize: 12.5,
                              fontWeight: 700,
                              opacity: 0.75,
                            }}
                          >
                            {text.open}
                          </Typography>
                        )}
                        <NorthWestRoundedIcon fontSize="small" />
                      </Box>
                    </ListItemButton>

                    {indexInGroup < group.items.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </Box>
          ))}
      </Container>
    </Box>
  );
}